import { useEffect, useRef, type ReactNode } from 'react';

/**
 * A small panel anchored under a header control.
 *
 * Deliberately not a dialog. These hold three or four choices and picking one is the whole
 * interaction, so trapping focus and dimming the page would be heavier than the decision being
 * made. What it does owe the user is the two ways out everyone expects: Escape, and clicking
 * somewhere else.
 *
 * The outside-click listener is registered on `pointerdown` rather than `click`. A `click` fires
 * after the button that opened the popover has already been released, so a listener added during
 * that same click would immediately see it and close what had just opened.
 */
export function Popover({
  id,
  label,
  onClose,
  children,
}: {
  readonly id: string;
  readonly label: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };

    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target;
      // The anchor is outside the panel, so a click on the button that opened this would count as
      // "outside". Leaving that to the button's own toggle keeps one behaviour in one place.
      if (target instanceof Node && panelRef.current?.contains(target) !== true) {
        onClose();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [onClose]);

  // Opening moves the focus in, so the choices are the next thing a keyboard reaches.
  useEffect(() => {
    panelRef.current?.querySelector<HTMLElement>('button, [href], input')?.focus();
  }, []);

  return (
    <div className="popover" id={id} role="group" aria-label={label} ref={panelRef}>
      <p className="popover__title">{label}</p>
      {children}
    </div>
  );
}

/** One choice inside a popover. The tick is the state; the label is never abbreviated. */
export function PopoverChoice({
  selected,
  onSelect,
  children,
}: {
  readonly selected: boolean;
  readonly onSelect: () => void;
  readonly children: ReactNode;
}) {
  return (
    <button
      type="button"
      className={`popover__choice${selected ? ' popover__choice--selected' : ''}`}
      aria-pressed={selected}
      onClick={onSelect}
    >
      {children}
    </button>
  );
}
